import React, {useEffect, useState, useRef} from 'react'
import Grid from '@mui/material/Grid'
import WbSwitch from '../../../components/common/WbSwitch'
import FormControlLabel from '@mui/material/FormControlLabel'
import {Autocomplete, Chip, FormControl, TextField} from '@mui/material'
import {getApiHookParams, normalizeParticipationFilter} from '../../../auth'

export default function SchoolFilter({
  participantFilter,
  setParticipantFilter,
  schools,
  updateInDb,
}) {
  const apiParams = getApiHookParams()

  // Separate state for toggle - independent of payload allSchools
  const [toggleOn, setToggleOn] = useState(false)

  // Ref to track if change is from user interaction (to prevent useEffect from overriding)
  const isUserInteraction = useRef(false)
  const hasInitialized = useRef(false)

  // Helper function to check if two arrays of school IDs are equal
  const areSchoolIdsEqual = (arr1, arr2) => {
    if (!arr1 || !arr2) return false
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false
    if (arr1.length !== arr2.length) return false
    const sorted1 = [...arr1].sort((a, b) => a - b)
    const sorted2 = [...arr2].sort((a, b) => a - b)
    return sorted1.every((id, index) => id === sorted2[index])
  }

  // Initialize toggle state on load - compare participantFilter.schoolId with apiParams.schoolIds
  // Only run on initial load, not when user interacts with toggle
  useEffect(() => {
    // Skip if this change was triggered by user interaction
    if (isUserInteraction.current) {
      isUserInteraction.current = false
      return
    }

    // Only initialize once when participantFilter is available
    if (participantFilter && !hasInitialized.current) {
      hasInitialized.current = true

      if (participantFilter.schoolId && apiParams.schoolIds && apiParams.schoolIds.length > 0) {
        const isEqual = areSchoolIdsEqual(participantFilter.schoolId, apiParams.schoolIds)
        setToggleOn(isEqual)
      } else if (!participantFilter.schoolId || participantFilter.schoolId.length === 0) {
        // If no schoolIds, default to toggle ON
        setToggleOn(true)
      } else {
        setToggleOn(false)
      }
    }
  }, [participantFilter, apiParams.schoolIds])

  // Single onChange handler for toggle
  const handleToggleChange = (e) => {
    const newToggleState = e.target.checked
    setToggleOn(newToggleState)

    // Mark as user interaction to prevent useEffect from overriding
    isUserInteraction.current = true

    // Determine payload allSchools value based on hasAllSchoolsAccess
    let payloadAllSchools
    if (apiParams.hasAllSchoolsAccess === false) {
      // If hasAllSchoolsAccess = false: always send false (regardless of toggle state)
      payloadAllSchools = false
    } else {
      // If hasAllSchoolsAccess = true: send toggle value (true when ON, false when OFF)
      payloadAllSchools = newToggleState
    }

    // Set schoolId based on toggle state
    // When toggle ON, select all accessible schools; when OFF, start with none selected
    const newSchoolId = newToggleState && apiParams.schoolIds && apiParams.schoolIds.length > 0
      ? [...apiParams.schoolIds]
      : []

    let val = {
      ...participantFilter,
      allSchools: payloadAllSchools,
      schoolId: newSchoolId,
    }

    // Normalize before setting state
    const normalizedVal = normalizeParticipationFilter(val, apiParams)
    // Ensure allSchools reflects the payload value (not overridden by normalization)
    normalizedVal.allSchools = payloadAllSchools

    setParticipantFilter(normalizedVal)
    updateInDb(normalizedVal)
  }

  return (
    !participantFilter?.all &&
    apiParams.groupId > 0 &&
    apiParams.schoolId === null && (
      <div className="participant-filter">
        <Grid container>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormControlLabel
              control={
                <WbSwitch
                  size="small"
                  checked={toggleOn}
                  onChange={handleToggleChange}
                ></WbSwitch>
              }
              label={'All Schools'}
            ></FormControlLabel>
          </Grid>

          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormControl
              fullWidth
              style={{
                display: toggleOn ? 'none' : 'block',
                marginTop: '10px',
              }}
            >
              <Autocomplete
                multiple
                disableCloseOnSelect
                disabled={toggleOn}
                className="school-filter-dd"
                options={schools}
                getOptionLabel={option => option.name ?? ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={schools.filter(s =>
                  (participantFilter?.schoolId ?? []).includes(s.id)
                )}
                onChange={(e, vals) => {
                  // When user selects schools from dropdown, toggle should be OFF
                  setToggleOn(false)

                  // Mark as user interaction to prevent useEffect from overriding
                  isUserInteraction.current = true

                  // Filter selected schools to only include those in apiParams.schoolIds
                  const selectedIds = vals.map(v => v.id)
                  const filteredIds = apiParams.schoolIds && apiParams.schoolIds.length > 0
                    ? selectedIds.filter(id => apiParams.schoolIds.includes(id))
                    : selectedIds

                  let val = {
                    ...participantFilter,
                    allSchools: false, // Explicitly set to false so normalization doesn't overwrite selection
                    schoolId: filteredIds,
                  }
                  // Normalize before setting state (updateInDb will handle allSchools logic)
                  const normalizedVal = normalizeParticipationFilter(val, apiParams)
                  setParticipantFilter(normalizedVal)
                  updateInDb(normalizedVal)
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, i) => (
                    <Chip
                      key={option.id}
                      label={option.name}
                      {...getTagProps({index: i})}
                    />
                  ))
                }
                renderInput={params => (
                  <TextField {...params} label="Select Schools" size="small" />
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
      </div>
    )
  )
}
